import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as Sentry from "@sentry/react-native";
import {
  ActivityIndicator,
  Alert,
  Platform,
  RefreshControl,
  StyleSheet,
  View,
  useWindowDimensions,
} from "react-native";
import { router } from "expo-router";
import { FlashListWithHeaders } from "@codeherence/react-native-header";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import ImageDetailsHeader from "./ImageDetailsHeader";
import ImageHeaderCard from "./Components/ImageActionCard";

import EmptyContentView from "@/Components/EmptyState/EmptyContentView";
import type { AppUserSession } from "@/schemas/auth/session";
import {
  type UserImageMedia,
  type UserLikableImageMedia,
} from "@/schemas/user/image";
import InteractionService from "@/services/user/interaction";
import type { UserProfilePreview } from "@/schemas/user/preview";
import UserListItem from "@/Components/Lists/UserListItem";
import type { UserRelationship } from "@/schemas/user/relationship";
import SkeletonUserListItem from "@/Components/Lists/SkeletonUserListItem";
import { createImageViewData, useTrackAnalytics } from "@/hooks/analytics";

type Props = {
  session: AppUserSession;
  imageMedia: UserLikableImageMedia;
};

function ImageDetailsScreen({ session, imageMedia }: Props): React.JSX.Element {
  const MAX_SCREEN_HEIGHT_RATIO = 0.63;

  const { top: safeAreaTop } = useSafeAreaInsets();
  const { height: screenHeight, width: screenWidth } = useWindowDimensions();
  const imageAspectRatio =
    imageMedia.image.attributes.width / imageMedia.image.attributes.height;
  const calculatedHeight = screenWidth / imageAspectRatio;
  const imageHeightToDisplay = Math.min(
    calculatedHeight,
    screenHeight * MAX_SCREEN_HEIGHT_RATIO,
  );
  const [image] = useState<UserImageMedia>(imageMedia.image);

  const [isLoadingImageLikes, setIsLoadingImageLikes] = useState(true);
  const [isLoadingMoreImageLikes, setIsLoadingMoreImageLikes] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [imageLikes, setImageLikes] = useState<UserProfilePreview[]>([]);
  const [refreshingImageLikes, setRefreshingImageLikes] = useState(false);

  // for scrolling to top when user clicks on header avatar
  // const flashListRef = useRef<Animated.FlatList>(null);
  // const scrollToTop = useCallback(() => {
  //   flashListRef.current?.scrollToOffset({ offset: 0, animated: true });
  // }, []);

  const interactionService = useMemo(
    () => new InteractionService(session),
    [session.token], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const fetchImageLikes = useCallback(async (): Promise<void> => {
    try {
      const response = await interactionService.getImageLikes(image, {
        limit: 10,
        next: null,
      });
      setImageLikes(response.items);
      setNextCursor(response.next);
    } catch (error) {
      Sentry.captureException(error);
      Alert.alert("Something went wrong", "Please try again later.", [
        { text: "OK" },
      ]);
    } finally {
      setIsLoadingImageLikes(false);
    }
  }, [interactionService, image]);

  useEffect(() => {
    fetchImageLikes();
  }, [fetchImageLikes]);

  // Track image view and its duration for analytics
  useTrackAnalytics(
    session.identity.username,
    (duration) => createImageViewData(image.id, duration),
    [image.id],
  );

  const onLoadMoreImageLikes = useCallback(async (): Promise<void> => {
    if (!nextCursor) return;
    if (!image) return;

    setIsLoadingMoreImageLikes(true);
    try {
      const response = await interactionService.getImageLikes(image, {
        limit: 10,
        next: nextCursor,
      });
      setImageLikes([...imageLikes, ...response.items]);
      setNextCursor(response.next);
    } catch (error) {
      Sentry.captureException(error);
      Alert.alert("Something went wrong", "Please try again later.", [
        { text: "OK" },
      ]);
    } finally {
      setIsLoadingMoreImageLikes(false);
    }
  }, [image, imageLikes, nextCursor, interactionService]);

  const handleRefreshImageLikes = async (): Promise<void> => {
    setRefreshingImageLikes(true);
    try {
      await Promise.all([
        fetchImageLikes(),
        // min refresh control spinner display time
        new Promise((resolve) => setTimeout(resolve, 500)),
      ]);
    } finally {
      setRefreshingImageLikes(false);
    }
  };

  const renderContent = (
    profile: UserProfilePreview,
    index: number,
  ): React.JSX.Element => {
    const user: UserRelationship = {
      id: "",
      rank: index,
      profile,
    };
    return <UserListItem session={session} user={user} />;
  };

  return (
    <>
      {image ? (
        <>
          <FlashListWithHeaders
            // ref={flashListRef}
            absoluteHeader
            HeaderComponent={(props) => (
              <ImageDetailsHeader
                {...props}
                session={session}
                imageMedia={imageMedia}
                imageDisplayHeight={imageHeightToDisplay}
                // onPressHeader={scrollToTop}
              />
            )}
            ListHeaderComponent={
              <ImageHeaderCard
                session={session}
                imageMedia={imageMedia}
                onLikeChange={() => handleRefreshImageLikes()}
              />
            }
            data={imageLikes}
            renderItem={({ item, index }) => renderContent(item, index)}
            containerStyle={styles.container}
            contentContainerStyle={{ paddingTop: imageHeightToDisplay }}
            onEndReached={onLoadMoreImageLikes}
            disableAutoFixScroll
            scrollEventThrottle={16}
            showsVerticalScrollIndicator={false}
            refreshControl={
              <RefreshControl
                refreshing={refreshingImageLikes}
                onRefresh={() => handleRefreshImageLikes()}
                progressViewOffset={Math.max(
                  0,
                  imageHeightToDisplay + safeAreaTop - 36,
                )}
                // use our own refresh control spinner on iOS since the build in one is not showing up when we have absolute positioned header
                tintColor="transparent" // iOS
              />
            }
            ListFooterComponent={
              isLoadingMoreImageLikes ? (
                <ActivityIndicator size={"large"} />
              ) : null
            }
            ListEmptyComponent={
              isLoadingImageLikes && image.likeCount > 0 ? (
                <View>
                  {Array.from({
                    length: Math.min(image.likeCount, 3),
                  }).map((_, index) => (
                    <SkeletonUserListItem key={index} />
                  ))}
                </View>
              ) : (
                <EmptyContentView
                  primaryMaterialCommunityIconName="heart-outline"
                  emptyTitle="No likes yet"
                  emptySubtitle="Be the first to like this image."
                />
              )
            }
          />
          {Platform.OS === "ios" && refreshingImageLikes && (
            <View
              style={[
                styles.refreshIndicator,
                { top: imageHeightToDisplay + 15 },
              ]}
            >
              <ActivityIndicator size="small" />
            </View>
          )}
        </>
      ) : (
        // TODO: this would not really happen because currently we are sending alert in notifications hook
        <EmptyContentView
          emptyTitle="This image is no longer available."
          primaryMaterialCommunityIconName="image-broken"
          actionButtonText="Go to Home Page"
          onActionButtonPress={() => {
            router.push({ pathname: "/explore/home" });
          }}
        />
      )}
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: "white",
  },
  refreshIndicator: {
    position: "absolute",
    left: 0,
    right: 0,
    alignItems: "center",
    zIndex: 10,
  },
});

export default ImageDetailsScreen;
